import {
  ArrayNotEmpty, IsArray, IsIn, IsNumber, IsOptional, IsString, ValidateNested,
} from 'class-validator';
import { Type } from 'class-transformer';
import { ISortOptions } from '../common/Search.types';
import {
  dataColNames, inventoryStatuses, labmatrixAttachmentEntities,
  labmatrixReportAttachmentTypes, lbAssays, lbAssayValues,
  specimenPurposes, subtypes,
} from './Labmatrix.constants';

export type DataColNames = typeof dataColNames[number];

export type SampleType = 'BMA' | 'PB' | 'TT';

export type InventoryStatus = typeof inventoryStatuses[number];

export type SpecimenPurpose = typeof specimenPurposes[number];

export type Subtype = typeof subtypes[number];

export type Assay = keyof typeof lbAssays;

export type FormEntry = {
  id: number;
  fields?: Record<string, string | boolean | Assay>
}

export type CreateFormEntry = {
  id?: number;
  locationPath?: string;
  fields?: Record<string | Assay, string | boolean>;
};

export interface IBiomaterial {
  id: number;
  name: string;
  parentId: number | null;
  lmSubjectId: string;
  subtype: Subtype;
  volume: string;
  containerType: string;
  collectionDate: string;
  specimenPurpose: SpecimenPurpose | null;
  inventoryStatus: InventoryStatus;
  storageLocation: string;
  dateSentForSequencing: string | null;
  assaysOrdered: Assay[] | null;
  downstreamProcess: Assay;
  qlimsId: string | null;
}

export interface IColumn {
  index: number;
  type: string;
}

export interface ILabmatrixBiomaterialsResp {
  data: string[][];
  columns: Record<DataColNames, IColumn>;
  variables: { 'Subject ID': string};
}

export interface IBiomaterialFilters extends ISortOptions {
  search?: string[];
  specimenPurpose?: (SpecimenPurpose | 'Not set')[];
  subtype?: (Subtype | 'Not set')[];
  inventoryStatus?: (InventoryStatus | 'Not set')[]
  containerType?: (string | 'Not set')[]
  hasFiles?: boolean;
}

export interface IGetBiomaterialByIdResp extends Record<string, unknown>{
  id: number;
  name: string;
  forms: Record<string, FormEntry[]>;
  studies: string[];
  subject: {id: number; code: string};
  parents: {id: number; barcode: string}[];
  type: SampleType;
  status: InventoryStatus;
  facility: string;
  volume: string;
  containerType: string;
  barcode: string;
  locationPath: string;
  batchId: string;
}

export type CreateBiomaterial = Partial<Omit<IGetBiomaterialByIdResp, 'forms'>> & {
  forms?: Record<string, CreateFormEntry[]>;
};

export interface ISubmitAssayData {
  biomaterialId: number;
  assays: Assay[];
}

export interface IForm {
  id?: number;
  name: string;
  updatedData: {fields: Partial<FormEntry['fields']> };
}

export class SubmitBiomaterialsAssaysBodyDTO implements ISubmitAssayData {
  @IsNumber()
  @Type(() => Number)
    biomaterialId: number;

  @IsArray()
  @ArrayNotEmpty()
  @IsIn(lbAssayValues, { each: true })
    assays: Assay[];
}

export type AssaySubmissionResult = {
  biomaterialId: number;
  status: 'success' | 'error';
  errorMessage: string | null;
};

export interface IClaritySubmissionBiomaterial {
  id: number;
  biomaterialId: number;
  assays: Assay[];
  status: 'success' | 'error';
  errorMessage: string | null;
}

export interface IClaritySubmission {
  id: number;
  lmSubjectId: string;
  name: string;
  createdAt: string;
  createdBy: string;
  items: IClaritySubmissionBiomaterial[];
}

export class SubmitBiomaterialsAssaysRequestDTO {
  @IsOptional()
  @IsString()
    name?: string;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => SubmitBiomaterialsAssaysBodyDTO)
    submissions: SubmitBiomaterialsAssaysBodyDTO[];
}

export type LabmatrixAttachmentEntity = typeof labmatrixAttachmentEntities[number];

// Response body of POST /api/:type/:id/attachments. Note that Labmatrix returns
// this JSON with a `Content-Type: text/plain` header (a documented workaround for
// an old IE bug), so it has to be parsed by hand rather than by the HTTP client.
export interface ILabmatrixAttachment {
  id: string;
  name: string;
  mimeType: string;
}

export type LabmatrixReportAttachmentType =
  typeof labmatrixReportAttachmentTypes[keyof typeof labmatrixReportAttachmentTypes];

export class UploadLabmatrixAttachmentDTO {
  @IsIn(labmatrixAttachmentEntities)
    entityType: LabmatrixAttachmentEntity;

  @IsNumber()
    entityId: number;

  // Written to the `Type` field of the CCI Confidential Attachment form record.
  @IsIn(Object.values(labmatrixReportAttachmentTypes))
    attachmentType: LabmatrixReportAttachmentType;

  // The report's own date, for the form's `Date` field. Defaults to now when absent.
  // `Original load date` is always the moment it was filed into Labmatrix.
  @IsOptional()
  @IsString()
    reportDate?: string;

  @IsString()
    fileName: string;

  // Base64-encoded file contents. The report PDF is passed inline rather than as
  // multipart so that the only multipart boundary is the Labmatrix edge itself.
  @IsString()
    fileBase64: string;

  @IsOptional()
  @IsString()
    contentType?: string;
}
